import { createFileRoute } from "@tanstack/react-router";
import { Container, CtaBand, Eyebrow, PrimaryLink, TextLink } from "@/components/site/primitives";
import { EXPERIENCE, SITE } from "@/lib/site-content";
import { ogImageMeta } from "@/lib/og";
import { Portrait } from "@/components/site/Portrait";

const title = "Timothy Ng | Fractional CTO and Founder of Scryla";
const description =
  "CTO, COO and technology consultancy leadership for 20+ years. Seed to £3m revenue, a 70% cost reduction, and work across fintech, iGaming, health and SaaS.";

export const Route = createFileRoute("/who-i-am")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE.url}/who-i-am` },
      ...ogImageMeta(
        "who-i-am",
        "Who I am | Timothy Ng, building and running technology businesses",
      ),
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/who-i-am` }],
  }),
  component: WhoIAmPage,
});

const IN_BRIEF = [
  ["Role", "CTO and COO experience"],
  ["Scryla", "Founded 2014"],
  ["Based", "Manchester, UK"],
];

const SECTORS = ["Fintech", "iGaming and gambling", "Digital health", "SaaS", "Data", "Agencies"];

const SELECTED_WORK: string[] = [
  "Growing a business from seed to £3 million revenue in less than three years with a corporate client and their senior management team.",
  "Working with private equity houses and private funds to raise capital investment for growth, series A and B.",
  "Implementing a cost reduction programme which reduced operational costs by 70%.",
  "Designing and developing a digital health care platform from inception to launch, combining remote consultations with doctor and internet pharmacy services.",
  "Providing strategic technology and information governance input in digital health, including current DSPT work, GDPR and ISO 27001, alongside earlier experience with the NHS Information Governance Toolkit it replaced.",
  "Coaching and mentoring CTOs, COOs and their teams.",
];

// Verified against the awarding bodies' published lists. The Prolific North
// shortlists name Novidian, not Scryla, so they are attributed to Novidian.
const AWARDS: { title: string; body: string }[] = [
  {
    title: "Best Tech SME Consultancy Firm 2024, UK",
    body: "Scryla, Innovation in Business Technology Innovator Awards 2024",
  },
  {
    title: "Tech Company of the Year: Small",
    body: "Novidian, Prolific North Tech Awards 2024 shortlist",
  },
  {
    title: "Tech Start-up of the Year",
    body: "Novidian, Prolific North Tech Awards 2025 shortlist",
  },
];

const APPROACH = [
  {
    title: "I tell you what I actually think",
    body: "Including when the answer is that you do not need me, or that the problem is not the one you think it is.",
  },
  {
    title: "I do not sell you a build",
    body: "I have no development team to keep busy and no supplier referrals to protect, so the advice has nothing attached to it.",
  },
  {
    title: "I write things down",
    body: "A conversation is useful. A written report your board and your development team can both read is more useful.",
  },
];

function WhoIAmPage() {
  return (
    <div>
      <section
        aria-labelledby="who-hero-heading"
        className="border-b border-border bg-gradient-to-b from-background to-surface"
      >
        <Container>
          <Eyebrow>Who I am</Eyebrow>
          <div className="mt-6 grid items-start gap-12 lg:grid-cols-12 lg:gap-16">
            <div className="min-w-0 lg:col-span-7">
              <h1
                id="who-hero-heading"
                className="text-[34px] leading-[1.06] font-bold tracking-[-0.035em] text-balance md:text-5xl"
              >
                Timothy Ng, founder of Scryla.
              </h1>
              <div className="mt-7 space-y-6 text-lg leading-relaxed text-body lg:text-xl">
                <p>
                  I have spent my career in the operating seats of technology businesses, primarily
                  as a Chief Technology Officer and also as a Chief Operating Officer. I have led
                  technology functions for more than twenty years, founded Scryla in 2014 and have
                  run it since.
                </p>
                <p>
                  That means when I sit down with a founder, I am not reading the situation from the
                  outside. I have carried a payroll, argued with a board, missed a deadline, and had
                  to decide what to cut. It is why my advice tends to be specific rather than
                  general.
                </p>
              </div>
              <div className="mt-9 flex flex-wrap gap-3">
                <PrimaryLink to="/contact">Get in touch</PrimaryLink>
                <a
                  href={SITE.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-border-strong bg-background px-6 py-3.5 text-[15px] font-semibold transition-colors hover:border-primary hover:text-primary"
                >
                  LinkedIn profile
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-[10px] border border-border bg-card p-7 shadow-panel md:p-9">
                <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-5 border-b border-hairline pb-7">
                  <Portrait displaySize={64} className="size-14 sm:size-16" />
                  <div className="min-w-0">
                    <p className="truncate text-[15px] font-semibold text-ink">Timothy Ng</p>
                    <p className="mt-0.5 truncate text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                      Founder, Scryla
                    </p>
                  </div>
                </div>

                <h2 className="mt-7 text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                  In brief
                </h2>
                <dl className="mt-5">
                  {IN_BRIEF.map(([term, value]) => (
                    <div
                      key={term}
                      className="grid gap-1 border-b border-hairline py-4 first:pt-0 sm:grid-cols-[6rem_minmax(0,1fr)] sm:items-baseline sm:gap-6"
                    >
                      <dt className="text-xs text-muted-foreground">{term}</dt>
                      <dd className="text-sm font-semibold text-ink sm:text-right">{value}</dd>
                    </div>
                  ))}
                  <div className="pt-5">
                    <dt className="text-xs text-muted-foreground">Sectors</dt>
                    <dd className="mt-3 flex flex-wrap gap-2">
                      {SECTORS.map((sector) => (
                        <span
                          key={sector}
                          className="rounded-sm border border-border bg-surface px-2 py-1 text-[11px] font-medium text-body"
                        >
                          {sector}
                        </span>
                      ))}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section aria-labelledby="approach-heading" className="border-b border-border">
        <Container>
          <Eyebrow>How I work with people</Eyebrow>
          <h2
            id="approach-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Plain, direct and on your side
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-3">
            {APPROACH.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-body">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section aria-labelledby="experience-heading" className="border-b border-border bg-surface">
        <Container>
          <Eyebrow>Experience</Eyebrow>
          <h2
            id="experience-heading"
            className="mt-3 text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Founder, operator, adviser
          </h2>
          <ol className="mt-8 divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {EXPERIENCE.map((item) => (
              <li
                key={`${item.org}-${item.period}`}
                className="grid grid-cols-[minmax(0,1fr)] gap-1 px-6 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-baseline sm:gap-6"
              >
                <div className="min-w-0">
                  <h3 className="text-[15px] font-semibold text-ink">{item.role}</h3>
                  <p className="text-sm text-body">
                    {item.org}
                    <span className="text-muted-foreground"> · {item.sector}</span>
                  </p>
                </div>

                <p className="shrink-0 text-sm text-muted-foreground sm:text-right">
                  {item.period}
                </p>
              </li>
            ))}
          </ol>
          <p className="mt-6 max-w-[62ch] text-[15px] leading-relaxed text-body">
            I also co-founded Novidian, a separate technology consultancy for engagements that need
            a broader team or capability than my work through Scryla.
          </p>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="selected-work-heading">
        <Container>
          <Eyebrow>Selected work</Eyebrow>
          <h2
            id="selected-work-heading"
            className="mt-3 max-w-[24ch] text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            Some of what that has looked like
          </h2>
          <ul className="mt-8 grid gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
            {SELECTED_WORK.map((item) => (
              <li key={item} className="bg-card p-6 text-[15px] leading-relaxed text-body">
                {item}
              </li>
            ))}
          </ul>
          <p className="mt-7 max-w-[70ch] text-[15px] leading-relaxed text-body">
            Where that digital health work is the reason you are reading this,{" "}
            <TextLink to="/fractional-cto-digital-health">
              Fractional CTO for digital health
            </TextLink>{" "}
            sets out how the technology and governance sides are handled together.
          </p>
        </Container>
      </section>

      <section className="border-b border-border bg-surface" aria-labelledby="awards-heading">
        <Container>
          <h2 id="awards-heading" className="text-2xl font-bold tracking-[-0.03em] md:text-[32px]">
            Awards and nominations
          </h2>
          <ul className="mt-8 grid gap-5 md:grid-cols-2">
            {AWARDS.map((item) => (
              <li key={item.title} className="rounded-lg border border-border bg-card p-6">
                <h3 className="text-[15px] font-semibold text-ink">{item.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{item.body}</p>
              </li>
            ))}
          </ul>
        </Container>
      </section>

      <section className="border-b border-border" aria-labelledby="wia-case-studies-heading">
        <Container>
          <h2
            id="wia-case-studies-heading"
            className="text-2xl font-bold tracking-[-0.03em] md:text-[32px]"
          >
            The work behind the record
          </h2>
          <p className="mt-4 max-w-[62ch] text-base leading-relaxed text-body md:text-[19px]">
            Three engagements written up in detail, what the situation was, what I did, and the
            outcomes the engagement records state.
          </p>
          <p className="mt-6 text-[15px]">
            <TextLink to="/case-studies">Read the case studies</TextLink>
          </p>
        </Container>
      </section>

      <CtaBand
        heading="Want to talk something through?"
        body="No pitch. Tell me what is happening and I will tell you what I would do."
        to="/contact"
        cta="Get in touch"
      />
    </div>
  );
}
